// Section for import npm components -----------------------------------------------------------------------------------
import React, { useState, useEffect } from 'react';
// Material UI ( mui ) components
import Box from '@mui/material/Box';

// Section for import custom components --------------------------------------------------------------------------------
import PrefecturesSelector from '@components/MansionNavigation/PrefecturesSelector';
import CitiesSelector from '@components/MansionNavigation/CitiesSelector';
import LinesSelector from '@components/MansionNavigation/LinesSelector';
import StationsSelector from '@components/MansionNavigation/StationsSelector';
import MansionsSelector from '@components/MansionNavigation/MansionsSelector';

import { MetaProvider } from '@providers/MansionNavigation/MetaProvider';
import { getPrefectures, loadPrefectures } from '@features/MansionNavigation/api/prefectures';
import RegionsDataJson from '@/config/data/regionItems.json';

// Section for import custom types -------------------------------------------------------------------------------------
import MansionNavigationProps from '@/types/components/MansionNavigation/MansionNavigationProps';
import PrefecturesData from '@/types/components/MansionNavigation/PrefecturesData';
import RegionsData from '@/types/components/MansionNavigation/RegionsData';

// Section for type definition -----------------------------------------------------------------------------------------

interface SecondNavigationProps {
  typeNavigation: string;
  prefecturesData: PrefecturesData;
  selectedPrefecture: {} | null;
  selectedLine: {} | null;
  selectedCity: {} | null;
  mansionsData: any;
}

function SecondNavigation({
  typeNavigation,
  prefecturesData,
  selectedPrefecture,
  selectedLine,
  selectedCity,
  mansionsData,
}: SecondNavigationProps) {
  let secondNavigation = null;

  switch (typeNavigation) {
    case 'area':
      secondNavigation = (
        <CitiesSelector
          prefecturesData={prefecturesData}
          selectedPrefecture={selectedPrefecture}
          modePrefSelect="prefectures"
        />
      );
      break;

    case 'line':
      secondNavigation = (
        <LinesSelector
          prefecturesData={prefecturesData}
          selectedPrefecture={selectedPrefecture}
          selectedLine={selectedLine}
          modePrefSelect="prefectures"
        />
      );
      break;

    case 'station':
      secondNavigation = (
        <StationsSelector
          prefecturesData={prefecturesData}
          selectedPrefecture={selectedPrefecture}
          selectedLine={selectedLine}
          modePrefSelect="prefectures"
        />
      );
      break;

    case 'mansion':
      secondNavigation = (
        <MansionsSelector
          prefecturesData={prefecturesData}
          selectedPrefecture={selectedPrefecture}
          selectedCity={selectedCity}
          mansionsData={mansionsData}
          modePrefSelect="prefectures"
        />
      );
      break;

    default:
      break;
  }

  return <Box>{secondNavigation}</Box>;
}

export default function MansionNavigation({
  isPrefectureSelect,
  selectedPrefecture,
  selectedCity,
  selectedLine,
  typeNavigation,
  mansionsData,
  navigationName,
}: MansionNavigationProps) {
  const targetId = navigationName ? `app-content-${navigationName}` : 'app-content';
  const container = document.getElementById(targetId)!;

  const [prefecturesData, setPrefecturesData] = useState<PrefecturesData>([]);

  const regionData: RegionsData = RegionsDataJson;

  useEffect(() => {
    const fetchPrefecturesData = async (): Promise<void> => {
      try {
        const data = await getPrefectures();
        const loadedPrefectures = await loadPrefectures(data);
        setPrefecturesData(loadedPrefectures);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch prefectures data:', error);
      }
    };
    fetchPrefecturesData().then();
  }, []);

  return (
    <MetaProvider initialMeta={container.dataset.meta ? JSON.parse(container.dataset.meta) : []}>
      <Box sx={{ width: '100%' }}>
        {isPrefectureSelect ? (
          <Box>
            <SecondNavigation
              typeNavigation={typeNavigation}
              prefecturesData={prefecturesData}
              selectedPrefecture={selectedPrefecture}
              selectedLine={selectedLine}
              selectedCity={selectedCity}
              mansionsData={mansionsData}
            />
          </Box>
        ) : (
          <Box>
            <PrefecturesSelector
              prefecturesData={prefecturesData}
              modePrefSelect="prefectures"
              regionData={regionData}
            />
          </Box>
        )}
      </Box>
    </MetaProvider>
  );
}
